import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Plus, Save, Edit2, Trash2, X, FileText, ChevronUp, ChevronDown, MapPin, Globe } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧', bg: 'bg-blue-50', text: 'text-blue-800' },
  { code: 'bg', name: 'Български', flag: '🇧🇬', bg: 'bg-green-50', text: 'text-green-800' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', bg: 'bg-yellow-50', text: 'text-yellow-800' }
];

export default function DestinationsManager() {
  const [routes, setRoutes] = useState([]);
  const [regions, setRegions] = useState([]);
  const [isRouteFormOpen, setIsRouteFormOpen] = useState(false);
  const [isRegionFormOpen, setIsRegionFormOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [editingRegion, setEditingRegion] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showPageContent, setShowPageContent] = useState(false);
  const [isSavingContent, setIsSavingContent] = useState(false);
  const [activeLang, setActiveLang] = useState('en');
  const [activeTab, setActiveTab] = useState('routes');
  
  const [pageContent, setPageContent] = useState({
    hero_subtitle_en: 'DESTINATIONS', hero_subtitle_bg: 'ДЕСТИНАЦИИ', hero_subtitle_de: 'ZIELE',
    hero_title_en: 'Connecting', hero_title_bg: 'Свързваме', hero_title_de: 'Wir verbinden',
    hero_title_highlight_en: 'Europe', hero_title_highlight_bg: 'Европа', hero_title_highlight_de: 'Europa',
    hero_description_en: 'We serve all major European destinations with regular routes and express options.',
    hero_description_bg: 'Обслужваме всички основни европейски дестинации с редовни маршрути и експресни опции.',
    hero_description_de: 'Wir bedienen alle wichtigen europäischen Ziele mit regelmäßigen Routen und Express-Optionen.',
    routes_subtitle_en: 'POPULAR ROUTES', routes_subtitle_bg: 'ПОПУЛЯРНИ МАРШРУТИ', routes_subtitle_de: 'BELIEBTE ROUTEN',
    routes_title_en: 'Main', routes_title_bg: 'Основни', routes_title_de: 'Haupt',
    routes_title_highlight_en: 'Transport Lines', routes_title_highlight_bg: 'транспортни линии', routes_title_highlight_de: 'Transportlinien',
    cta_title_en: 'Destination Not Listed?', cta_title_bg: 'Дестинацията не е в списъка?', cta_title_de: 'Ziel nicht aufgeführt?',
    cta_button_en: 'Contact Us', cta_button_bg: 'Свържете се с нас', cta_button_de: 'Kontaktieren Sie uns'
  });
  
  const defaultRouteForm = {
    name_en: '', name_bg: '', name_de: '',
    time_en: '', time_bg: '', time_de: '',
    frequency_en: '', frequency_bg: '', frequency_de: '',
    via_en: '', via_bg: '', via_de: '',
    order: 0, is_active: true
  };
  
  const defaultRegionForm = {
    name_en: '', name_bg: '', name_de: '',
    countries_en: '', countries_bg: '', countries_de: '',
    emoji: '🌍',
    order: 0, is_active: true
  };
  
  // Common emoji options for regions
  const EMOJI_OPTIONS = ['🌍', '🌎', '🌏', '🇪🇺', '🗺️', '📍', '🧭', '✈️', '🚛', '🏔️', '🌊', '🏖️', '❄️', '☀️'];
  
  const [routeForm, setRouteForm] = useState(defaultRouteForm);
  const [regionForm, setRegionForm] = useState(defaultRegionForm);

  useEffect(() => { fetchData(); fetchPageContent(); }, []);

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/destinations-page-content`);
      if (response.data && Object.keys(response.data).length > 0) {
        setPageContent(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) { console.error('Error:', error); }
  };

  const handleSavePageContent = async () => {
    setIsSavingContent(true);
    try {
      await axios.put(`${API}/admin/destinations-page-content`, pageContent);
      toast.success('Page content saved!');
    } catch (error) { toast.error('Error saving'); }
    finally { setIsSavingContent(false); }
  };

  const fetchData = async () => {
    try {
      const [routesRes, regionsRes] = await Promise.all([
        axios.get(`${API}/admin/destination-routes`),
        axios.get(`${API}/admin/destination-regions`)
      ]);
      setRoutes(routesRes.data.routes || []);
      setRegions(regionsRes.data.regions || []);
    } catch (error) {
      console.error('Error fetching destinations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Route handlers
  const handleRouteSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRoute) {
        await axios.put(`${API}/admin/destination-routes/${editingRoute.id}`, routeForm);
        toast.success('Route updated!');
      } else {
        await axios.post(`${API}/admin/destination-routes`, routeForm);
        toast.success('Route added!');
      }
      fetchData();
      setIsRouteFormOpen(false);
      setEditingRoute(null);
      setRouteForm(defaultRouteForm);
    } catch (error) {
      toast.error('Error saving route');
    }
  };

  const handleDeleteRoute = async (id) => {
    if (!window.confirm('Delete this route?')) return;
    try {
      await axios.delete(`${API}/admin/destination-routes/${id}`);
      toast.success('Route deleted!');
      fetchData();
    } catch (error) {
      toast.error('Error deleting route');
    }
  };

  const startEditRoute = (route) => {
    setEditingRoute(route);
    setRouteForm({
      name_en: route.name_en || route.name || '',
      name_bg: route.name_bg || '',
      name_de: route.name_de || '',
      time_en: route.time_en || route.time || '',
      time_bg: route.time_bg || '',
      time_de: route.time_de || '',
      frequency_en: route.frequency_en || route.frequency || '',
      frequency_bg: route.frequency_bg || '',
      frequency_de: route.frequency_de || '',
      via_en: route.via_en || route.via || '',
      via_bg: route.via_bg || '',
      via_de: route.via_de || '',
      order: route.order || 0,
      is_active: route.is_active !== false
    });
    setIsRouteFormOpen(true);
  };

  // Region handlers
  const handleRegionSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...regionForm,
        countries_en: regionForm.countries_en.split(',').map(c => c.trim()).filter(c => c),
        countries_bg: regionForm.countries_bg.split(',').map(c => c.trim()).filter(c => c),
        countries_de: regionForm.countries_de.split(',').map(c => c.trim()).filter(c => c)
      };
      if (editingRegion) {
        await axios.put(`${API}/admin/destination-regions/${editingRegion.id}`, submitData);
        toast.success('Region updated!');
      } else {
        await axios.post(`${API}/admin/destination-regions`, submitData);
        toast.success('Region added!');
      }
      fetchData();
      setIsRegionFormOpen(false);
      setEditingRegion(null);
      setRegionForm(defaultRegionForm);
    } catch (error) {
      toast.error('Error saving region');
    }
  };

  const handleDeleteRegion = async (id) => {
    if (!window.confirm('Delete this region?')) return;
    try {
      await axios.delete(`${API}/admin/destination-regions/${id}`);
      toast.success('Region deleted!');
      fetchData();
    } catch (error) {
      toast.error('Error deleting region');
    }
  };

  const startEditRegion = (region) => {
    setEditingRegion(region);
    setRegionForm({
      name_en: region.name_en || region.name || '',
      name_bg: region.name_bg || '',
      name_de: region.name_de || '',
      countries_en: Array.isArray(region.countries_en) ? region.countries_en.join(', ') : '',
      countries_bg: Array.isArray(region.countries_bg) ? region.countries_bg.join(', ') : '',
      countries_de: Array.isArray(region.countries_de) ? region.countries_de.join(', ') : '',
      emoji: region.emoji || '🌍',
      order: region.order || 0,
      is_active: region.is_active !== false
    });
    setIsRegionFormOpen(true);
  };

  const updatePageField = (field, value) => setPageContent(prev => ({ ...prev, [field]: value }));

  if (isLoading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Page Content Toggle */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-4">
          <button onClick={() => setShowPageContent(!showPageContent)} className="w-full flex items-center justify-between text-left">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#C41E3A]" />
              <span className="font-bold text-slate-900">Destinations Page Content</span>
            </div>
            {showPageContent ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {showPageContent && (
            <div className="mt-4 border-t pt-4">
              <Tabs defaultValue="en" className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  {LANGUAGES.map(lang => (
                    <TabsTrigger key={lang.code} value={lang.code}>{lang.flag} {lang.name}</TabsTrigger>
                  ))}
                </TabsList>
                
                {LANGUAGES.map(lang => (
                  <TabsContent key={lang.code} value={lang.code}>
                    <div className={`p-4 rounded-2xl space-y-3 ${lang.bg}`}>
                      <h4 className={`font-bold ${lang.text}`}>{lang.flag} {lang.name}</h4>
                      <div className="grid grid-cols-2 gap-2">
                        <Input value={pageContent[`hero_subtitle_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_subtitle_${lang.code}`, e.target.value)} placeholder="Subtitle" className="bg-white" />
                        <Input value={pageContent[`hero_title_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_title_${lang.code}`, e.target.value)} placeholder="Title" className="bg-white" />
                      </div>
                      <Input value={pageContent[`hero_title_highlight_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_title_highlight_${lang.code}`, e.target.value)} placeholder="Title Highlight" className="bg-white" />
                      <Textarea value={pageContent[`hero_description_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_description_${lang.code}`, e.target.value)} placeholder="Description" className="bg-white" rows={2} />
                      <div className="grid grid-cols-2 gap-2">
                        <Input value={pageContent[`cta_title_${lang.code}`] || ''} onChange={(e) => updatePageField(`cta_title_${lang.code}`, e.target.value)} placeholder="CTA Title" className="bg-white" />
                        <Input value={pageContent[`cta_button_${lang.code}`] || ''} onChange={(e) => updatePageField(`cta_button_${lang.code}`, e.target.value)} placeholder="CTA Button" className="bg-white" />
                      </div>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
              <Button onClick={handleSavePageContent} disabled={isSavingContent} className="w-full bg-[#C41E3A] hover:bg-[#A01830] mt-4">
                <Save className="w-4 h-4 mr-2" /> {isSavingContent ? 'Saving...' : 'Save Page Content'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Routes / Regions Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 mb-4">
          <TabsTrigger value="routes">🛣️ Routes ({routes.length})</TabsTrigger>
          <TabsTrigger value="regions">🌍 Regions ({regions.length})</TabsTrigger>
        </TabsList>

        {/* Routes Tab */}
        <TabsContent value="routes">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-slate-900">Transport Routes</h2>
            <Button onClick={() => { setIsRouteFormOpen(true); setEditingRoute(null); setRouteForm(defaultRouteForm); }} className="bg-[#C41E3A] hover:bg-[#A01830] rounded-full">
              <Plus className="w-4 h-4 mr-2" /> Add Route
            </Button>
          </div>

          {isRouteFormOpen && (
            <Card className="mb-6 bg-white rounded-3xl shadow-lg border-0">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold">{editingRoute ? 'Edit Route' : 'New Route'}</h3>
                  <Button variant="ghost" size="sm" onClick={() => setIsRouteFormOpen(false)}><X className="w-4 h-4" /></Button>
                </div>
                
                <form onSubmit={handleRouteSubmit} className="space-y-4">
                  <Tabs value={activeLang} onValueChange={setActiveLang}>
                    <TabsList className="grid w-full grid-cols-3 mb-4">
                      {LANGUAGES.map(lang => (
                        <TabsTrigger key={lang.code} value={lang.code} className="data-[state=active]:bg-slate-800 data-[state=active]:text-white">
                          {lang.flag} {routeForm[`name_${lang.code}`] && '✓'}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                    
                    {LANGUAGES.map(lang => (
                      <TabsContent key={lang.code} value={lang.code}>
                        <div className={`p-4 rounded-2xl space-y-3 ${lang.bg}`}>
                          <Input value={routeForm[`name_${lang.code}`]} onChange={(e) => setRouteForm({...routeForm, [`name_${lang.code}`]: e.target.value})} placeholder="Route Name (e.g., Bulgaria - Germany)" className="bg-white" required={lang.code === 'en'} />
                          <div className="grid grid-cols-2 gap-2">
                            <Input value={routeForm[`time_${lang.code}`]} onChange={(e) => setRouteForm({...routeForm, [`time_${lang.code}`]: e.target.value})} placeholder="Time (e.g., 24-36 hours)" className="bg-white" />
                            <Input value={routeForm[`frequency_${lang.code}`]} onChange={(e) => setRouteForm({...routeForm, [`frequency_${lang.code}`]: e.target.value})} placeholder="Frequency (e.g., Daily)" className="bg-white" />
                          </div>
                          <Input value={routeForm[`via_${lang.code}`]} onChange={(e) => setRouteForm({...routeForm, [`via_${lang.code}`]: e.target.value})} placeholder="Via (e.g., Hungary, Austria)" className="bg-white" />
                        </div>
                      </TabsContent>
                    ))}
                  </Tabs>
                  
                  <div className="flex flex-wrap gap-4 items-center pt-4 border-t">
                    <div className="flex items-center gap-4">
                      <Input type="number" value={routeForm.order} onChange={(e) => setRouteForm({...routeForm, order: parseInt(e.target.value) || 0})} placeholder="Order" className="w-24" />
                      <label className="flex items-center gap-2"><input type="checkbox" checked={routeForm.is_active} onChange={(e) => setRouteForm({...routeForm, is_active: e.target.checked})} /> Active</label>
                    </div>
                    <div className="flex gap-2 ml-auto">
                      <Button type="button" variant="outline" onClick={() => setIsRouteFormOpen(false)}>Cancel</Button>
                      <Button type="submit" className="bg-[#C41E3A] hover:bg-[#A01830]"><Save className="w-4 h-4 mr-2" /> Save Route</Button>
                    </div>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          <div className="space-y-3">
            {routes.map(route => (
              <Card key={route.id} className={`bg-white rounded-2xl shadow-sm ${!route.is_active ? 'opacity-60' : ''}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <MapPin className="w-8 h-8 text-[#C41E3A]" />
                  <div className="flex-1">
                    <h4 className="font-bold">{route.name_en || route.name}</h4>
                    <div className="flex gap-1 mt-1">
                      {LANGUAGES.map(lang => (
                        <span key={lang.code} className={`text-xs px-1.5 py-0.5 rounded ${route[`name_${lang.code}`] ? lang.bg : 'bg-slate-100 text-slate-400'}`}>{lang.flag}</span>
                      ))}
                    </div>
                    <p className="text-sm text-slate-500">{route.time_en} • {route.frequency_en}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => startEditRoute(route)}><Edit2 className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => handleDeleteRoute(route.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Regions Tab */}
        <TabsContent value="regions">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-slate-900">Coverage Regions</h2>
            <Button onClick={() => { setIsRegionFormOpen(true); setEditingRegion(null); setRegionForm(defaultRegionForm); }} className="bg-[#C41E3A] hover:bg-[#A01830] rounded-full">
              <Plus className="w-4 h-4 mr-2" /> Add Region
            </Button>
          </div>

          {isRegionFormOpen && (
            <Card className="mb-6 bg-white rounded-3xl shadow-lg border-0">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold">{editingRegion ? 'Edit Region' : 'New Region'}</h3>
                  <Button variant="ghost" size="sm" onClick={() => setIsRegionFormOpen(false)}><X className="w-4 h-4" /></Button>
                </div>
                
                <form onSubmit={handleRegionSubmit} className="space-y-4">
                  <Tabs value={activeLang} onValueChange={setActiveLang}>
                    <TabsList className="grid w-full grid-cols-3 mb-4">
                      {LANGUAGES.map(lang => (
                        <TabsTrigger key={lang.code} value={lang.code} className="data-[state=active]:bg-slate-800 data-[state=active]:text-white">
                          {lang.flag} {regionForm[`name_${lang.code}`] && '✓'}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                    
                    {LANGUAGES.map(lang => (
                      <TabsContent key={lang.code} value={lang.code}>
                        <div className={`p-4 rounded-2xl space-y-3 ${lang.bg}`}>
                          <Input value={regionForm[`name_${lang.code}`]} onChange={(e) => setRegionForm({...regionForm, [`name_${lang.code}`]: e.target.value})} placeholder="Region Name (e.g., Central Europe)" className="bg-white" required={lang.code === 'en'} />
                          <Textarea value={regionForm[`countries_${lang.code}`]} onChange={(e) => setRegionForm({...regionForm, [`countries_${lang.code}`]: e.target.value})} placeholder="Countries (comma separated)" className="bg-white" rows={2} />
                        </div>
                      </TabsContent>
                    ))}
                  </Tabs>
                  
                  {/* Emoji selector */}
                  <div className="pt-4 border-t">
                    <Label className="text-sm font-medium mb-2 block">Region Icon (Emoji)</Label>
                    <div className="flex flex-wrap gap-2">
                      {EMOJI_OPTIONS.map(em => (
                        <button
                          key={em}
                          type="button"
                          onClick={() => setRegionForm({...regionForm, emoji: em})}
                          className={`text-2xl p-2 rounded-lg border-2 transition-all ${regionForm.emoji === em ? 'border-[#C41E3A] bg-red-50' : 'border-transparent hover:bg-slate-100'}`}
                        >
                          {em}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Current: {regionForm.emoji || '🌍'}</p>
                  </div>
                  
                  <div className="flex flex-wrap gap-4 items-center pt-4 border-t">
                    <div className="flex items-center gap-4">
                      <Input type="number" value={regionForm.order} onChange={(e) => setRegionForm({...regionForm, order: parseInt(e.target.value) || 0})} placeholder="Order" className="w-24" />
                      <label className="flex items-center gap-2"><input type="checkbox" checked={regionForm.is_active} onChange={(e) => setRegionForm({...regionForm, is_active: e.target.checked})} /> Active</label>
                    </div>
                    <div className="flex gap-2 ml-auto">
                      <Button type="button" variant="outline" onClick={() => setIsRegionFormOpen(false)}>Cancel</Button>
                      <Button type="submit" className="bg-[#C41E3A] hover:bg-[#A01830]"><Save className="w-4 h-4 mr-2" /> Save Region</Button>
                    </div>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {regions.map(region => (
              <Card key={region.id} className={`bg-white rounded-2xl shadow-sm ${!region.is_active ? 'opacity-60' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <span className="text-3xl">{region.emoji || '🌍'}</span>
                    <div className="flex-1">
                      <h4 className="font-bold">{region.name_en || region.name}</h4>
                      <div className="flex gap-1 mt-1">
                        {LANGUAGES.map(lang => (
                          <span key={lang.code} className={`text-xs px-1.5 py-0.5 rounded ${region[`name_${lang.code}`] ? lang.bg : 'bg-slate-100 text-slate-400'}`}>{lang.flag}</span>
                        ))}
                      </div>
                      <p className="text-sm text-slate-500 mt-1">{(region.countries_en || []).join(', ')}</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => startEditRegion(region)}><Edit2 className="w-4 h-4" /></Button>
                    <Button variant="outline" size="sm" onClick={() => handleDeleteRegion(region.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
